/**
 * Dismissible "upgrade your plan" banner for the dashboard.
 *
 * Persona-aware free→paid conversion nudge:
 *   - shown only to logged-in users with NO active breeder plan
 *   - buyers are excluded (they get <BuyerPassPromo/> instead)
 *   - dismiss = 14-day localStorage snooze, not a permanent hide
 *   - when the admin "founding member" promo is live, the banner
 *     surfaces the promo code for extra urgency
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { SparkleIcon as Sparkle, XIcon as X, ArrowRightIcon as ArrowRight, TagIcon as Tag } from "@phosphor-icons/react";
import { isNativeApp } from "@/lib/nativeBridge";

const SNOOZE_KEY = "plan_promo_snooze_until";
const SNOOZE_DAYS = 14;

export default function BreederPlanPromo() {
  const { user } = useAuth() || {};
  const [show, setShow] = useState(false);
  const [promo, setPromo] = useState(null);
  // App Store 3.1.1 — no subscription promos inside the native shell.
  const native = isNativeApp();

  useEffect(() => {
    if (native) return;
    if (!user) return;
    try {
      const until = parseInt(localStorage.getItem(SNOOZE_KEY) || "0", 10);
      if (until && Date.now() < until) return;
    } catch { /* private mode */ }
    api.get("/auth/me")
      .then((r) => { if (!r.data?.active_plan) setShow(true); })
      .catch(() => {});
    api.get("/public/founding-promo")
      .then((r) => { if (r.data?.enabled) setPromo(r.data); })
      .catch(() => {});
  }, [user]);

  if (native || !show) return null;

  const dismiss = () => {
    setShow(false);
    try {
      localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_DAYS * 86400000));
    } catch { /* private mode */ }
  };

  return (
    <div
      className="relative mb-8 rounded-2xl border border-gold/40 bg-gradient-to-br from-[#FBF7EC] to-white p-5 sm:p-6"
      data-testid="plan-promo-banner"
    >
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute top-3 right-3 text-[#9CA3AF] hover:text-ink"
        data-testid="plan-promo-dismiss"
      >
        <X size={16} weight="bold" />
      </button>
      <div className="flex items-start gap-4 flex-col sm:flex-row sm:items-center">
        <div className="flex-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#8A6A00] flex items-center gap-1.5">
            <Sparkle size={12} weight="fill" /> You&rsquo;re on the free plan
          </p>
          <h3 className="font-spectral text-xl tracking-tight mt-1.5">
            Go Pro — unlimited registrations, Dog Log included, lower fees
          </h3>
          <p className="text-sm text-[#6B6B6B] mt-1 leading-relaxed">
            $44/mo covers unlimited dog &amp; litter registrations, the Verified
            Pro wax-seal on every listing, Dog Log free, and a 1% platform-fee
            discount on every sale.
          </p>
          {promo && (
            <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-ink text-gold px-3 py-1 text-xs font-bold" data-testid="plan-promo-code">
              <Tag size={12} weight="fill" /> {promo.message}
            </p>
          )}
        </div>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-1.5 rounded-full bg-ink hover:bg-crimson text-white px-5 py-2.5 text-sm font-semibold transition-colors flex-shrink-0"
          data-testid="plan-promo-cta"
        >
          Compare plans <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}
